import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { getIn } from 'formik';
import noop from 'lodash/noop';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import FormHelperText from '@mui/material/FormHelperText';

const RTAutocomplete = (props) => {
    const {
        field,
        filter,
        form: { touched, errors, setFieldValue },
        options,
        getOptionLabel,
        getOptionSelected,
        textFieldProps,
        placeholder,
        title,
        icon,
        name,
        label,
        fullWidth,
        className,
        classes,
        endAdornment,
        handleChangeCallback,
        onlyChangeCallback,
        onChangeText,
        autoFocus,
        isDisplayTextError,
        helperText,
        freeSolo,
        required,
        disabled,
        renderOption: callerRenderOption,
        ...autoCompleteProps
    } = props;

    const mergedTextFieldProps = {
        ...RTAutocomplete.defaultProps.textFieldProps,
        ...textFieldProps
    };

    const filterOptions = createFilterOptions({
        stringify: filter
    });

    const errorText = getIn(errors, field.name);
    const touchedVal = getIn(touched, field.name);
    const hasError = touchedVal && errorText !== undefined;

    const onChange = useCallback(
        (_, value) => {
            if (!onlyChangeCallback) setFieldValue(field.name, value);
            handleChangeCallback(value);
        },
        [onlyChangeCallback, setFieldValue, field.name, handleChangeCallback]
    );

    return (
        <FormControl variant="standard" fullWidth={fullWidth} component="div">
            <Autocomplete
                freeSolo={freeSolo}
                value={field.value && field.value._id ? field.value : null}
                name={field.name}
                openOnFocus
                filterOptions={filter ? filterOptions : (option) => option}
                options={options || []}
                autoComplete
                getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;
                    if (typeof option.name === 'string') return option.name;
                    return option?.label || option?.code || '';
                }}
                onChange={onChange}
                className={className}
                classes={classes}
                isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    return (
                        option._id === value._id ||
                        option.name === value.name ||
                        option?.code === value?.code
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...mergedTextFieldProps}
                        autoFocus={autoFocus}
                        onChange={onChangeText}
                        error={hasError}
                        helperText={hasError && !helperText ? errorText : helperText}
                        label={label}
                        required={required}
                        placeholder={placeholder}
                        variant="outlined"
                        disabled={disabled}
                        inputProps={params.inputProps}
                        InputProps={{
                            ref: params.InputProps.ref,
                            endAdornment: endAdornment ? (
                                <InputAdornment position="end">{endAdornment}</InputAdornment>
                            ) : (
                                params.InputProps.endAdornment
                            ),
                            startAdornment: icon !== null && title !== '' ? (
                                <InputAdornment position="start">
                                    {icon}
                                    <span>{title}</span>
                                </InputAdornment>
                            ) : params.InputProps.startAdornment
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                )}
                {...autoCompleteProps}
                {...(callerRenderOption ? {
                    renderOption: (props, option) => {
                        const { key, ...rest } = props;
                        return <li key={key} {...rest}>{callerRenderOption(option)}</li>;
                    }
                } : {})}
            />
            {errors && touched[name] && isDisplayTextError && (
                <FormHelperText>{errors[name]}</FormHelperText>
            )}
        </FormControl>
    );
};

RTAutocomplete.defaultProps = {
    freeSolo: false,
    autoFocus: false,
    onChangeText: noop,
    onlyChangeCallback: false,
    handleChangeCallback: () => {},
    endAdornment: null,
    filter: (option) => (option?.name ? option.name : ''),
    getOptionLabel: (option) =>
        typeof option === 'object'
            ? option?.name || option?.label || option?.code || ''
            : String(option || ''),
    getOptionSelected: (option, value) =>
        !!option &&
        !!value &&
        (option._id === value._id ||
            option.name === value.name ||
            option?.code === value?.code),
    textFieldProps: {
        required: false,
        fullWidth: true
    },
    fullWidth: false,
    placeholder: '',
    title: '',
    icon: null,
    margin: 'none',
    name: '',
    label: '',
    className: '',
    classes: {},
    isDisplayTextError: true,
    helperText: '',
    required: false,
    disabled: false
};

RTAutocomplete.propTypes = {
    freeSolo: PropTypes.bool,
    autoFocus: PropTypes.bool,
    onChangeText: PropTypes.func,
    onlyChangeCallback: PropTypes.bool,
    handleChangeCallback: PropTypes.func,
    endAdornment: PropTypes.element,
    field: PropTypes.shape({
        name: PropTypes.string,
        // eslint-disable-next-line react/forbid-prop-types
        value: PropTypes.any
    }).isRequired,
    margin: PropTypes.oneOf(['none', 'dense', 'normal']),
    form: PropTypes.shape({
        dirty: PropTypes.bool,
        errors: PropTypes.shape({}),
        setFieldValue: PropTypes.func,
        touched: PropTypes.shape({})
    }).isRequired,
    placeholder: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    name: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.element,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            _id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
    ).isRequired,
    filter: PropTypes.func,
    getOptionLabel: PropTypes.func,
    getOptionSelected: PropTypes.func,
    fullWidth: PropTypes.bool,
    textFieldProps: PropTypes.shape({
        label: PropTypes.string,
        required: PropTypes.bool,
        fullWidth: PropTypes.bool,
        margin: PropTypes.oneOf(['none', 'dense', 'normal'])
    }),
    className: PropTypes.string,
    classes: PropTypes.shape({}),
    isDisplayTextError: PropTypes.bool,
    helperText: PropTypes.string,
    required: PropTypes.bool,
    disabled: PropTypes.bool
};

export default memo(RTAutocomplete);